import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Users as UsersIcon, Loader2, Info, ShieldPlus, UserPlus,
    ChevronDown, MailPlus, GitBranch, UserCheck, BarChart2, UserLock, ShieldCheck,
    Cog,
    Pencil
} from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { DataTable2 } from '../components/ui/DataTable2';
import { useToast } from '../hooks/useToast';
import { fetchNamedUsers, fetchAuditLogs, fetchCurrentEndpointPermissions, fetchEmployeeList, fetchRoles, client, fetchStoresWhereStaffAssigned, fetchAllBankAccounts, fetchSettings, fetchPaymentProcessors, fetchTerminalsForStore, API_ROOT_PATH } from '../apiHelper';
import Dropdown from '../components/ui/Dropdown';
import { activeAnimations } from 'framer-motion';
import Modal from '../components/ui/Modal';
import Select from 'react-select'
import Input from '../components/ui/Input';
import Checkbox from '../components/ui/Checkbox';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import DropZone from '../components/ui/DropZone';
import { API_BASE_PATH } from '../apiHelper';

/**
 * Helper function to determine and display the member's primary type.
 * Combines information from member_type, register_type, and profile_type.
 * @param {object} member - The member object from the API response.
 * @returns {string} A human-readable string representing the member's type.
 */
const getMemberTypeDisplay = (member) => {
    let primaryType = '';

    // Prioritize based on the most specific profile type
    if (member.user?.profile_type === 'E') {
        primaryType = 'Employee';
    } else if (member.user?.profile_type === 'C') {
        primaryType = 'Customer';
    } else if (member.member_type === 'O') { // Check for Organization type
        primaryType = 'Business Organization';
    } else if (member.user?.profile_type === 'B') {
        primaryType = 'Business User'; // For individual users within a business context
    } else {
        primaryType = 'General User'; // Fallback for any other user
    }

    // Check for Admin status from register_type
    if (member.user?.register_type === 'A') {
        // If it's an Admin, append (Admin) unless it's already an Organization which implies admin capabilities
        if (primaryType === 'Business Organization') {
            return 'Business Organization (Admin)';
        }
        return `${primaryType} (Admin)`;
    }

    return primaryType;
};

/**
 * Helper function to translate member_state to a human-readable status.
 * @param {number} state - The member_state numerical value.
 * @returns {string} A human-readable string representing the member's status.
 */
const getMemberStatusDisplay = (state) => {
    switch (state) {
        case 1:
            return 'Verified';
        case 2:
            return 'Unverified';
        case 3:
            return 'Not Applicable';
        case 4:
            return 'Suspended';
        default:
            return 'Unknown';
    }
};

const Users = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // State for the logged-in user and members list
    const [user, setUser] = useState(null);
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [errorMembers, setErrorMembers] = useState(null);

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [loadingStores, setLoadingStores] = useState(false);

    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [portalAddress, setPortalAddress] = useState('');
    const [modalSelectedStore, setModalSelectedStore] = useState('');
    const [savingSettings, setSavingSettings] = useState(false);

    // State for activity logs and their loading status
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingAuditLogs, setLoadingAuditLogs] = useState(true);
    const [errorAuditLogs, setErrorAuditLogs] = useState(null);

    // state for endpoint permissions list and loading status
    const [endpointPermissions, setEndpointPermissions] = useState([]);
    const [loadingEndpointPermissions, setLoadingEndpointPermissions] = useState(true);
    const [errorEndpointPermissions, setErrorEndpointPermissions] = useState(null);

    // state for the roles administration list
    const [roles, setRoles] = useState([])
    const [loadingRoles, setLoadingRoles] = useState(true)
    const [errorRoles, setErrorRoles] = useState(null)

    // State to manage the active tab
    const [activeTab, setActiveTab] = useState('users');

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedEndpoint, setSelectedEndpoint] = useState(null)
    const [availableRoles, setAvailableRoles] = useState([])
    const [selectedRoles, setSelectedRoles] = useState([])

    const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [newRoleDescription, setNewRoleDescription] = useState('');
    const [newRoleEditable, setNewRoleEditable] = useState(true);
    const [creatingRole, setCreatingRole] = useState(false);

    const [psKey, setPsKey] = useState('');
    const [modalSelectedStorePs, setModalSelectedStorePs] = useState('');
    const [isPsModalOpen, setIsPsModalOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [ppKey, setPpKey] = useState('');
    const [modalSelectedStorePp, setModalSelectedStorePp] = useState('');
    const [isPpModalOpen, setIsPpModalOpen] = useState(false);
    const [showPpPassword, setShowPpPassword] = useState(false);

    // State for Paystack Payment Gateway configuration section
    const [bankAccountOptions, setBankAccountOptions] = useState([]);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
    const [errorBankAccounts, setErrorBankAccounts] = useState(null);
    const [isPaystackModalOpen, setIsPaystackModalOpen] = useState(false);
    const [modalSelectedStorePsGateway, setModalSelectedStorePsGateway] = useState('');
    const [selectedBankAccount, setSelectedBankAccount] = useState('');

    const [globalSettings, setGlobalSettings] = useState([]);
    const [loadingSettings, setLoadingSettings] = useState(false);
    const [paymentProcessors, setPaymentProcessors] = useState([]);

    const [psKeyName, setPsKeyName] = useState('PSKey');
    const [ppKeyName, setPpKeyName] = useState('PPKey');

    const [revenueRecognition, setRevenueRecognition] = useState('');
    const [modalSelectedStoreRev, setModalSelectedStoreRev] = useState('');
    const [isRevModalOpen, setIsRevModalOpen] = useState(false);
    const [revKeyName, setRevKeyName] = useState('RevenueRecognitionType');

    const [gatewayName, setGatewayName] = useState('Paystack');

    const [isHeaderModalOpen, setIsHeaderModalOpen] = useState(false);
    const [modalSelectedStoreHeader, setModalSelectedStoreHeader] = useState('');
    const [invoiceHeaderData, setInvoiceHeaderData] = useState({
        attachmenturl: '',
        filename: '',
        mimetype: '',
        mimetypeencoding: '',
        filesize: 0
    });

    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
    const [terminalSn, setTerminalSn] = useState('');
    const [modalSelectedStoreTerminal, setModalSelectedStoreTerminal] = useState('');
    const [isTerminalModalOpen, setIsTerminalModalOpen] = useState(false);

    const [isCreateTerminalModalOpen, setIsCreateTerminalModalOpen] = useState(false);
    const [createTerminalStoreId, setCreateTerminalStoreId] = useState('');
    const [newTerminalId, setNewTerminalId] = useState('');
    const [newTerminalSn, setNewTerminalSn] = useState('');
    const [creatingTerminal, setCreatingTerminal] = useState(false);

    // State for store terminals dropdown in assignment modal
    const [storeTerminals, setStoreTerminals] = useState([]);
    const [loadingStoreTerminals, setLoadingStoreTerminals] = useState(false);
    const [selectedPosterminalId, setSelectedPosterminalId] = useState('');

    // State for POS Terminal to Bank Account Settlement Mapping
    const [isPosProcessorModalOpen, setIsPosProcessorModalOpen] = useState(false);
    const [modalSelectedStorePosProc, setModalSelectedStorePosProc] = useState('');
    const [selectedMappingTerminalId, setSelectedMappingTerminalId] = useState('');
    const [selectedMappingBankAccount, setSelectedMappingBankAccount] = useState('');
    const [posProcessorsList, setPosProcessorsList] = useState([]);

    const loadStoreTerminals = async (storeIdToFetch) => {
        if (!storeIdToFetch) return;
        setLoadingStoreTerminals(true);
        try {
            const data = await fetchTerminalsForStore(storeIdToFetch);
            console.log(data)
            setStoreTerminals(Array.isArray(data) ? data : []);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to retrieve store terminals.", "error");
            setStoreTerminals([]);
        } finally {
            setLoadingStoreTerminals(false);
        }
    };

    const storeTerminalOptions = useMemo(() => {
        return storeTerminals.map(t => ({
            label: `${t.store_identifier}: (ID: ${t.terminal_id}) (SN: ${t.terminal_sn})`,
            value: t.posterminal_id || t.terminal_id
        }));
    }, [storeTerminals]);

    const handleSavePosTerminalProcessor = useCallback(async () => {
        if (!modalSelectedStorePosProc) {
            showToast("Please select a store/branch.", "error");
            return;
        }
        if (!selectedMappingTerminalId) {
            showToast("Please select a POS terminal.", "error");
            return;
        }
        if (!selectedMappingBankAccount) {
            showToast("Please select a bank account.", "error");
            return;
        }

        setSavingSettings(true);
        try {
            const payload = {
                store_id: modalSelectedStorePosProc,
                posterminal_id: selectedMappingTerminalId,
                bankaccount_id: selectedMappingBankAccount
            };

            // console.log(payload)

            const response = await client.postData('/store/map-terminal-to-bank-account', payload);
            showToast(response.msg || "POS terminal settlement mapping updated successfully!", "success");

            setIsPosProcessorModalOpen(false);
            setModalSelectedStorePosProc('');
            setSelectedMappingTerminalId('');
            setSelectedMappingBankAccount('');
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to save POS terminal bank mapping.", "error");
        } finally {
            setSavingSettings(false);
        }
    }, [modalSelectedStorePosProc, selectedMappingTerminalId, selectedMappingBankAccount, showToast]);

    const handleCreatePosTerminal = async () => {
        if (!createTerminalStoreId) {
            showToast("Please select a store.", "error");
            return;
        }
        if (!newTerminalId.trim()) {
            showToast("Terminal ID cannot be empty.", "error");
            return;
        }
        if (!newTerminalSn.trim()) {
            showToast("Terminal Serial Number cannot be empty.", "error");
            return;
        }

        setCreatingTerminal(true);
        try {
            const payload = {
                store_id: createTerminalStoreId,
                terminal_id: newTerminalId.trim(),
                terminal_sn: newTerminalSn.trim()
            };
            const response = await client.postData('/store/create-terminal-for-store', payload);
            showToast(response.msg || "POS Terminal allocated successfully!", "success");

            setIsCreateTerminalModalOpen(false);
            setCreateTerminalStoreId('');
            setNewTerminalId('');
            setNewTerminalSn('');
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to create POS terminal.", "error");
        } finally {
            setCreatingTerminal(false);
        }
    };

    const loadGlobalSettings = useCallback(async () => {
        setLoadingSettings(true);
        try {
            const settings = await fetchSettings();
            setGlobalSettings(settings || []);
        } catch (err) {
            console.error(err.response?.data?.detail || "Failed to load global settings", err);
            showToast(err.response?.data?.detail || "Failed to load global settings", "error");
        } finally {
            setLoadingSettings(false);
        }
    }, []);

    const loadPaymentProcessors = useCallback(async () => {
        try {
            const processors = await fetchPaymentProcessors();
            // console.log(processors);
            setPaymentProcessors(processors || []);
        } catch (err) {
            console.error(err.response?.data?.detail || "Failed to load payment processors", err);
            showToast(err.response?.data?.detail || "Failed to load payment processors", "error");
        }
    }, []);

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to view user management.", "info");
            navigate('/login'); // Uncomment to redirect if not logged in
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast]);

    // fetch roles
    const loadRoles = useCallback(async () => {
        setLoadingRoles(true)
        setErrorRoles(null)
        try {
            const roles = await fetchRoles()
            setRoles(roles)
            setAvailableRoles(roles.map(role => ({
                value: role.role_id,
                label: role.name
            })))
        } catch (err) {
            const errorMessage = err.response?.data?.detail || "Failed to load roles"
            setErrorRoles(errorMessage)
            showToast(errorMessage, "error")
        } finally {
            setLoadingRoles(false)
        }
    }, [user, showToast])

    const employeeOptions = useMemo(() => {
        return employees.map(e => {
            const fullName = [e.first_name, e.middle_name, e.last_name]
                .filter(Boolean)
                .join(' ');

            return {
                label: fullName,
                value: e.users_id
            };
        });
    }, [employees]);

    const loadEmployees = async (storeIdToFetch) => {
        if (!storeIdToFetch) return;

        setLoadingEmployees(true);
        try {
            const data = await fetchEmployeeList(storeIdToFetch);
            // console.log(data)
            setEmployees(Array.isArray(data) ? data : []);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to retrieve employee profiles.", "error");
            setEmployees([]);
        } finally {
            setLoadingEmployees(false);
        }
    };

    useEffect(() => {
        if (modalSelectedStoreTerminal && isTerminalModalOpen) {
            setSelectedEmployeeId('');
            setSelectedPosterminalId('');
            loadEmployees(modalSelectedStoreTerminal);
            loadStoreTerminals(modalSelectedStoreTerminal);
        }
    }, [modalSelectedStoreTerminal, isTerminalModalOpen]);

    const loadAssignedStores = useCallback(async () => {
        if (!user) return;
        setLoadingStores(true);
        try {
            const fetchedStores = await fetchStoresWhereStaffAssigned(user);
            const mappedStores = fetchedStores.filter(s => s.storetype !== 'WHS').map(store => ({
                label: store.identifier,
                value: store.store_id,
            }));
            setStores(mappedStores);
            if (fetchedStores.length > 0) {
                setSelectedStoreId(fetchedStores[0].store_id);
            }
        } catch (err) {
            console.error("Error loading assigned stores:", err);
            showToast(err.response?.data?.detail || "Failed to load assigned stores.", "error");
        } finally {
            setLoadingStores(false);
        }
    }, [user, showToast]);

    // Fetch members when component mounts or user changes
    const loadMembers = useCallback(async () => {
        if (!user) return;
        setLoadingMembers(true);
        setErrorMembers(null);
        try {
            const fetchedMembers = await fetchNamedUsers(user);
            setMembers(fetchedMembers);
        } catch (err) {
            console.error("Error loading members:", err);
            const errorMessage = err.response?.data?.detail || `Failed to load members: ${err.message || err}`;
            setErrorMembers(errorMessage);
            showToast(errorMessage, "error");
        } finally {
            setLoadingMembers(false);
        }
    }, [user, showToast]);

    // New function to fetch activity logs
    const loadAuditLogs = useCallback(async () => {
        if (!user) return;
        setLoadingAuditLogs(true);
        setErrorAuditLogs(null);
        try {
            // Call the new API helper without any parameters as requested
            const fetchedLogs = await fetchAuditLogs();
            setAuditLogs(fetchedLogs);
        } catch (err) {
            console.error(err.response?.data?.detail || "Error loading activity logs:", err);
            const errorMessage = err.response?.data?.detail || `Failed to load activity logs: ${err.message || err}`;
            setErrorAuditLogs(errorMessage);
            showToast(errorMessage, "error");
        } finally {
            setLoadingAuditLogs(false);
        }
    }, [user, showToast]);

    const loadCurrentEndpointPermissions = useCallback(async () => {
        if (!user) return;
        setLoadingEndpointPermissions(true)
        setErrorEndpointPermissions(null)

        try {
            const fetchedPermissions = await fetchCurrentEndpointPermissions();
            setEndpointPermissions(fetchedPermissions)
        } catch (err) {
            console.error(err.response?.data?.detail || "Error loading endpoint permissions")
            const errorMessage = err.response?.data?.detail || `Failed to load current endpoint permissions list: ${err.message || err}`;
            setErrorEndpointPermissions(errorMessage)
            showToast(errorMessage, "error")
        } finally {
            setLoadingEndpointPermissions(false)
        }
    }, [user, showToast])

    const loadBankAccounts = useCallback(async () => {
        setLoadingBankAccounts(true);
        setErrorBankAccounts(null);
        try {
            const fetchedAccounts = await fetchAllBankAccounts();
            const formattedOptions = fetchedAccounts.map(account => ({
                label: `${account.name} (${account.account_id})`,
                value: account.account_id
            }));
            setBankAccountOptions(formattedOptions);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load bank accounts: ${err.message || err}`;
            setErrorBankAccounts(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setLoadingBankAccounts(false);
        }
    }, [showToast]);

    useEffect(() => {
        if (activeTab === 'settings') {
            loadBankAccounts();
            loadGlobalSettings();
            loadPaymentProcessors();
        }
    }, [activeTab, loadBankAccounts, loadGlobalSettings, loadPaymentProcessors]);

    const getSettingValue = (storeId, keyName) => {
        const setting = globalSettings.find(s => s.store_id === Number(storeId) && s.attrkey === keyName);
        return setting ? (setting.stringvalue || setting.numericvalue || setting.integervalue || setting.datetimevalue || '') : '';
    }

    const getPaystackBankAccount = (storeId) => {
        const processor = paymentProcessors.find(p => p.store_id === Number(storeId) && p.name === 'Paystack');
        return processor ? processor.bankaccount_id : '';
    }

    const getPaymentProcessorsForStore = (storeId) => {
        return paymentProcessors.filter(p => p.store_id === Number(storeId));
    }

    const getBankAccountForGateway = (storeId, name) => {
        const processor = paymentProcessors.find(
            p => p.store_id === Number(storeId) && p.name.toLowerCase() === name.toLowerCase()
        );
        return processor ? processor.bankaccount_id : '';
    }

    // Trigger loading members and activity logs when user is available
    useEffect(() => {
        loadMembers();
        loadAuditLogs();
        loadCurrentEndpointPermissions();
        loadRoles();
        loadAssignedStores();
    }, [loadMembers, loadAuditLogs, loadCurrentEndpointPermissions, loadRoles, loadAssignedStores]);

    // Handlers for user actions (unchanged)
    const handleEditUser = useCallback((row) => {
        showToast(`Editing user: ${row.user?.first_name || ''} ${row.user?.last_name || ''}`, 'info');
    }, [showToast]);

    const handleDeleteUser = useCallback((row) => {
        if (window.confirm(`Are you sure you want to delete user: ${row.user?.first_name || ''} ${row.user?.last_name || ''}?`)) {
            showToast(`Deleting user: ${row.user?.first_name || ''} ${row.user?.last_name || ''}`, 'warning');
        }
    }, [showToast]);

    const handleResetPassword = useCallback((row) => {
        if (window.confirm(`Are you sure you want to reset password for: ${row.user?.first_name || ''} ${row.user?.last_name || ''}?`)) {
            showToast(`Resetting password for: ${row.user?.first_name || ''} ${row.user?.last_name || ''}`, 'info');
        }
    }, [showToast]);

    const handleViewUserDetails = useCallback(async row => {
        showToast(`Viewing details for ${row.user?.first_name || ''} ${row.user?.last_name || ''}`, 'info');
        try {
            const response = await client.fetchData(`/users/user-details/${row.member_id}`);
            // console.log(response) // FULL USER PROFILE 
            navigate('/settings/user-profile', {
                state: { profileData: response }
            });
        } catch (err) {
            showToast(err.response?.data?.detail || 'Error viewing user details.', 'error');
        }
    }, [client, showToast]);

    const handleAttachLineOfCredit = useCallback((row) => {
        showToast(`Attaching line of credit for: ${row.user?.first_name || ''} ${row.user?.last_name || ''}`, 'info');
        navigate('/accounting/line-of-credit', {
            state: {
                customerId: row.member_id,
                customerName: `${row.user?.first_name || ''} ${row.user?.last_name || ''}`.trim()
            }
        });
    }, [showToast, navigate]);

    const handleRenewInvitation = useCallback(async (row) => {
        showToast(`Renewing invitation for ${row.user?.first_name || ''} ${row.user?.last_name || ''}`.trim() || row.user?.credential?.logon_id, 'info');
        try {
            const payload = {
                member_id: row.member_id,
                member_type: row.member_type,
                register_type: row.user?.register_type,
                profile_type: row.user?.profile_type,
                logon_id: row.user?.credential?.logon_id,
                first_name: row.user?.first_name,
                last_name: row.user?.last_name,
            }
            const response = await client.postData('/auth/renew-invitation', payload);
            showToast(response.msg, 'info');
        } catch (err) {
            console.error("Error renewing invitation", err);
            const errorMessage = err.response?.data?.detail || `Failed to renew invitation: ${err.message || err}`;
            setTimeout(() => showToast(errorMessage, 'error'), 0)
        }
    }, [showToast]);

    const handleRoleAction = useCallback((key, row) => {
    }, [])

    const handlePermissionAction = useCallback((key, row) => {
        if (key === 'modify_permissions') {
            setSelectedEndpoint(row)
            setSelectedRoles(row.required_roles?.map(role => ({
                value: role,
                label: roles.find(r => r.role_id == +role).name
            })) || []
            );
            setIsModalOpen(true)
        }
    }, [roles])

    const handleRoleSelect = (selectedOptions) => {
        setSelectedRoles(selectedOptions || [])
    }

    const handleSavePermissions = useCallback(async () => {
        try {
            const updatedPermissions = {
                docstring: selectedEndpoint.docstring,
                endpoint_path: selectedEndpoint.endpoint_path,
                http_method: selectedEndpoint.http_method,
                required_roles: selectedRoles.map(role => role.label),
            }
            // console.log(updatedPermissions)
            const response = await client.postData('/auth/edit-permissions', updatedPermissions)
            setTimeout(() => showToast(response.msg || `Successfully updated ${selectedEndpoint.endpoint_path} permissions`, "success"), 0);
            setIsModalOpen(false)
            loadCurrentEndpointPermissions()
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to update permissions", "error")
        }
    }, [selectedEndpoint, selectedRoles, showToast, loadCurrentEndpointPermissions]);

    const handleUserAction = useCallback((key, row) => {
        switch (key) {
            case 'edit':
                handleEditUser(row);
                break;
            case 'delete':
                handleDeleteUser(row);
                break;
            case 'reset_password':
                handleResetPassword(row);
                break;
            case 'view_details':
                handleViewUserDetails(row);
                break;
            case 'attach_line_of_credit':
                handleAttachLineOfCredit(row);
                break;
            case 'renew_invitation':
                handleRenewInvitation(row);
                break;
            default:
                console.warn(`Unknown action: ${key}`);
        }
    }, [handleEditUser, handleDeleteUser, handleViewUserDetails, handleResetPassword, handleAttachLineOfCredit, handleRenewInvitation, showToast]);

    const handleSavePortalAddress = useCallback(async () => {
        if (!modalSelectedStore) {
            showToast("Please select a branch/store.", "error");
            return;
        }
        if (!portalAddress.trim()) {
            showToast("Portal address cannot be empty.", "error");
            return;
        }

        setSavingSettings(true);
        try {
            const payload = {
                store_id: modalSelectedStore,
                key: "branch_portal_address",
                value: portalAddress.trim()
            };

            // console.log(payload)

            // Adjust endpoint route matching your platform architecture rules
            const response = await client.postData('/store/save-setting', payload);
            showToast(response.msg || "Portal address updated successfully!", "success");

            setIsSettingsModalOpen(false);
            setPortalAddress('');
            setModalSelectedStore('');
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to save portal address.", "error");
        } finally {
            setSavingSettings(false);
        }
    }, [modalSelectedStore, portalAddress, showToast]);

    const handleSavePsKey = useCallback(async () => {
        if (!modalSelectedStorePs) {
            showToast("Please select a branch/store.", "error");
            return;
        }
        if (!psKeyName.trim()) {
            showToast("Key name attribute cannot be empty.", "error");
            return;
        }
        if (!psKey.trim()) {
            showToast("Key value cannot be empty.", "error");
            return;
        }

        setSavingSettings(true);
        try {
            const payload = {
                store_id: modalSelectedStorePs,
                key: psKeyName.trim(), // Dynamic key parameter
                value: psKey.trim()
            };

            const response = await client.postData('/store/save-setting', payload);
            showToast(response.msg || `${psKeyName} updated successfully!`, "success");

            setIsPsModalOpen(false);
            setPsKey('');
            setModalSelectedStorePs('');
        } catch (err) {
            showToast(err.response?.data?.detail || `Failed to save ${psKeyName}.`, "error");
        } finally {
            setSavingSettings(false);
        }
    }, [modalSelectedStorePs, psKeyName, psKey, showToast]);

    const handleSavePpKey = useCallback(async () => {
        if (!modalSelectedStorePp) {
            showToast("Please select a branch/store.", "error");
            return;
        }
        if (!ppKeyName.trim()) {
            showToast("Key name attribute cannot be empty.", "error");
            return;
        }
        if (!ppKey.trim()) {
            showToast("Key value cannot be empty.", "error");
            return;
        }

        setSavingSettings(true);
        try {
            const payload = {
                store_id: modalSelectedStorePp,
                key: ppKeyName.trim(), // Dynamic key parameter
                value: ppKey.trim()
            };

            const response = await client.postData('/store/save-setting', payload);
            showToast(response.msg || `${ppKeyName} updated successfully!`, "success");

            setIsPpModalOpen(false);
            setPpKey('');
            setModalSelectedStorePp('');
        } catch (err) {
            showToast(err.response?.data?.detail || `Failed to save ${ppKeyName}.`, "error");
        } finally {
            setSavingSettings(false);
        }
    }, [modalSelectedStorePp, ppKeyName, ppKey, showToast]);

    const handleSaveTerminalAssignment = useCallback(async () => {
        if (!modalSelectedStoreTerminal) {
            showToast("Please select a store.", "error");
            return;
        }
        if (!selectedEmployeeId) {
            showToast("Please select an employee.", "error");
            return;
        }
        if (!selectedPosterminalId) {
            showToast("Please select a POS terminal.", "error");
            return;
        }

        setSavingSettings(true);
        try {
            const payload = {
                store_id: modalSelectedStoreTerminal,
                users_id: selectedEmployeeId,
                posterminal_id: selectedPosterminalId,
            };

            // console.log(payload)

            const response = await client.postData('/store/terminal-assignment', payload);
            showToast(response.msg || "Terminal assigned successfully!", "success");

            setIsTerminalModalOpen(false);
            setSelectedPosterminalId('');
            setSelectedEmployeeId('');
            setModalSelectedStoreTerminal('');
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to assign terminal.", "error");
        } finally {
            setSavingSettings(false);
        }
    }, [modalSelectedStoreTerminal, selectedEmployeeId, selectedPosterminalId, showToast]);

    // Automatically sync PSKey modal input when store or key name changes
    useEffect(() => {
        if (modalSelectedStorePs && psKeyName) {
            setPsKey(getSettingValue(modalSelectedStorePs, psKeyName));
        }
    }, [modalSelectedStorePs, psKeyName, globalSettings]);

    // Automatically sync PPKey modal input when store or key name changes
    useEffect(() => {
        if (modalSelectedStorePp && ppKeyName) {
            setPpKey(getSettingValue(modalSelectedStorePp, ppKeyName));
        }
    }, [modalSelectedStorePp, ppKeyName, globalSettings]);

    useEffect(() => {
        if (modalSelectedStoreRev && revKeyName) {
            setRevenueRecognition(getSettingValue(modalSelectedStoreRev, revKeyName));
        }
    }, [modalSelectedStoreRev, revKeyName, globalSettings]);

    const roleLabelMap = useMemo(() => {
        const map = new Map();
        roles.forEach(r => map.set(r.role_id, r.name));
        return map;
    }, [roles]);   // `roles` comes from fetchRoles()

    // Define columns for DataTable2 for the roles list
    const rolesColumns = useMemo(() => [
        {
            key: 'role_id',
            label: 'ID',
        },
        {
            key: 'name',
            label: 'Name',
            searchable: (row) => row.name
        },
        {
            key: 'description',
            label: 'Description',
            searchable: (row) => row.description
        }
    ], [handleRoleAction])

    const rolesRowActions = useMemo(() => [
        {
            label: 'Edit Role', key: "edit_role",
            shouldRender: (row) => row.editable !== false,
        }
    ], [])

    const isInvitationExpired = (member) => {
        // console.log(member?.user)
        const cred = member?.user?.credential;
        if (!cred) return false;

        if (cred.invite_used === 1) return false;

        if (!cred.expires_at) return false;

        const expiryDate = new Date(cred.expires_at);
        const now = new Date();

        return expiryDate < now;
    }

    // Define columns for DataTable2 for the members list
    const memberColumns = useMemo(() => [
        {
            key: 'name',
            label: 'Name',
            render: (value, row) => `${row.user?.first_name || ''} ${row.user?.last_name || ''}`.trim(),
            searchable: (row) => `${row.user?.first_name || ''} ${row.user?.last_name || ''}`.trim()
        },
        {
            key: 'email',
            label: 'Email / Logon ID',
            render: (value, row) => row.user?.credential?.logon_id || 'N/A',
            searchable: (row) => row.user?.credential?.logon_id || 'N/A'
        },
        {
            key: 'roles',
            label: 'Roles',
            render: (value, row) => {
                const roles = row.memberroles?.map(mr => mr.role?.name).filter(Boolean).join(', ');
                return roles || 'No Roles';
            },
            searchable: (row) => row.memberroles?.map(mr => mr.role?.name).filter(Boolean).join(', ') || 'No Roles'
        },
        {
            key: 'type',
            label: 'User Type',
            render: (value, row) => getMemberTypeDisplay(row),
            searchable: (row) => getMemberTypeDisplay(row)
        },
        {
            key: 'status',
            label: 'Status',
            render: (value, row) => getMemberStatusDisplay(row.member_state),
            searchable: (row) => getMemberStatusDisplay(row.member_state)
        },
    ], [handleUserAction]);

    const handleSavePaystackGateway = useCallback(async () => {
        if (!modalSelectedStorePsGateway) {
            showToast("Please select a branch/store.", "error");
            return;
        }
        // Validation for gateway name
        if (!gatewayName.trim()) {
            showToast("Please enter a gateway name.", "error");
            return;
        }
        if (!selectedBankAccount) {
            showToast("Please select a bank account.", "error");
            return;
        }

        setSavingSettings(true);
        try {
            const payload = {
                store_id: modalSelectedStorePsGateway,
                name: gatewayName.trim(),
                bankaccount_id: selectedBankAccount
            };

            // console.log(payload);

            const response = await client.postData('/sales/save-payment-processor', payload);
            showToast(response.msg || "Gateway settings updated successfully!", "success");

            setIsPaystackModalOpen(false);
            setSelectedBankAccount('');
            setModalSelectedStorePsGateway('');
            setGatewayName('Paystack');
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to save Paystack settings.", "error");
        } finally {
            setSavingSettings(false);
        }
    }, [modalSelectedStorePsGateway, gatewayName, selectedBankAccount, showToast]);

    const handleSaveRevSetting = useCallback(async () => {
        if (!modalSelectedStoreRev) {
            showToast("Please select a branch/store.", "error");
            return;
        }
        if (!revKeyName.trim()) {
            showToast("Key name attribute cannot be empty.", "error");
            return;
        }

        if (!revenueRecognition) {
            showToast("Please select a recognition period type.", "error");
            return;
        }

        setSavingSettings(true);
        try {
            const payload = {
                store_id: modalSelectedStoreRev,
                key: revKeyName.trim(),
                value: revenueRecognition && revenueRecognition !== ';' ? revenueRecognition : null
            };

            // console.log(payload);

            const response = await client.postData('/store/save-setting', payload);
            showToast(response.msg || `${revKeyName} updated successfully!`, "success");

            setIsRevModalOpen(false);
            setRevenueRecognition('');
            setModalSelectedStoreRev('');
        } catch (err) {
            showToast(err.response?.data?.detail || `Failed to save ${revKeyName}.`, "error");
        } finally {
            setSavingSettings(false);
        }
    }, [modalSelectedStoreRev, revKeyName, revenueRecognition, showToast]);

    // Define row actions for the members list
    const userRowActions = useMemo(() => [
        { label: "View Details", key: "view_details" },
        // { label: "Edit User", key: "edit" },
        { label: "Reset Password", key: "reset_password" },
        // { label: "Delete User", key: "delete" },
        {
            label: "Attach Line of Credit",
            key: "attach_line_of_credit",
            shouldRender: (row) => row.user?.profile_type === 'C'
        },
        {
            label: "Renew Invitation",
            key: "renew_invitation",
            shouldRender: (row) => isInvitationExpired(row)
        },
    ], [isInvitationExpired]);

    // Define columns for the permissions list table
    const permissionsListColumns = useMemo(() => [
        {
            key: 'http_method',
            label: 'Request Method',
            searchable: (row) => row.http_method
        },
        {
            key: 'endpoint_path',
            label: 'Endpoint URL',
            searchable: (row) => row.endpoint_path
        },
        {
            key: 'docstring',
            label: 'Description',
            searchable: (row) => row.docstring
        },
        {
            key: 'required_roles',
            label: 'Required Roles',
            render: (value) => {
                return value && value.length > 0 ? value.join(', ') : 'N/A'
            }
        }
    ], [handlePermissionAction])

    const permissionRowActions = useMemo(() => [
        { label: "Modify permissions", key: "modify_permissions" },
    ], [])

    // New columns for the audit log table
    const auditLogColumns = useMemo(() => [
        {
            key: 'audit_timestamp',
            label: 'Timestamp',
            render: (value) => new Date(value).toLocaleString(),
            searchable: (row) => new Date(row.audit_timestamp).toLocaleString()
        },
        {
            key: 'event_type',
            label: 'Event',
            searchable: (row) => row.event_type
        },
        {
            key: 'user_name',
            label: 'User Name',
            render: (value, row) => `${row.user?.first_name || ''} ${row.user?.last_name || ''}`.trim(),
            searchable: (row) => `${row.user?.first_name || ''} ${row.user?.last_name || ''}`.trim()
        },
        {
            key: 'user_id',
            label: 'User ID',
            render: (value, row) => row.users_id,
            searchable: (row) => String(row.users_id)
        },
        {
            key: 'session_id',
            label: 'Session ID',
            searchable: (row) => row.session_id
        },
    ], []);

    const handleCreateRole = useCallback(async () => {
        if (!newRoleName.trim()) {
            showToast("Role name is required.", "error");
            return;
        }

        setCreatingRole(true);
        try {
            const payload = {
                name: newRoleName.trim(),
                description: newRoleDescription.trim(),
                editable: newRoleEditable,
            };

            // console.log(payload)

            const response = await client.postData('/auth/create-role', payload);
            showToast(response.msg || "Role created successfully!", "success");

            // Reset form and close modal
            setNewRoleName('');
            setNewRoleDescription('');
            setNewRoleEditable(true);
            setIsCreateRoleModalOpen(false);

            // Reload roles to show the new one
            loadRoles();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || "Failed to create role";
            showToast(errorMessage, "error");
        } finally {
            setCreatingRole(false);
        }
    }, [newRoleName, newRoleDescription, newRoleEditable, showToast, loadRoles]);

    // Helper function to validate image aspect ratio (between 4.0 and 4.5)
    const validateHeaderAspectRatio = (fileOrUrl) => {
        return new Promise((resolve) => {
            const img = new Image();
            const isFile = fileOrUrl instanceof File;
            const objectUrl = isFile ? URL.createObjectURL(fileOrUrl) : fileOrUrl;

            img.onload = () => {
                if (isFile) URL.revokeObjectURL(objectUrl);
                const width = img.naturalWidth;
                const height = img.naturalHeight;
                const aspectRatio = height > 0 ? width / height : 0;

                // Logging actual calculated size and aspect ratio
                console.log(`[Image Ratio Check] Width: ${width}px, Height: ${height}px, Aspect Ratio: ${aspectRatio.toFixed(4)}`);

                const isValid = aspectRatio >= 4.0 && aspectRatio <= 4.5;
                resolve({ isValid, aspectRatio: aspectRatio.toFixed(2), width, height });
            };

            img.onerror = () => {
                if (isFile) URL.revokeObjectURL(objectUrl);
                console.error("[Image Ratio Check] Failed to load image element.");
                resolve({ isValid: false, aspectRatio: "0.00", width: 0, height: 0 });
            };

            img.src = objectUrl;
        });
    };

    const handleInvoiceHeaderUploadComplete = useCallback((uploadedFiles) => {
        if (uploadedFiles && uploadedFiles.length > 0) {
            const fileData = uploadedFiles[0];
            setInvoiceHeaderData({
                attachmenturl: fileData.url,
                filename: fileData.filename,
                mimetype: fileData.mimetype,
                mimetypeencoding: fileData.encoding,
                filesize: fileData.size
            });
            showToast("Invoice header validated and uploaded successfully!", "success");
        }
    }, [showToast]);

    const handleSaveInvoiceHeader = useCallback(async () => {
        if (!modalSelectedStoreHeader) {
            showToast("Please select a store/branch.", "error");
            return;
        }
        if (!invoiceHeaderData.attachmenturl) {
            showToast("Please upload an invoice header image first.", "error");
            return;
        }

        setSavingSettings(true);

        // Pre-flight check: ensure unified aspect ratio validation passes
        const imageUrl = `${API_ROOT_PATH}${invoiceHeaderData.attachmenturl}`;
        const { isValid, aspectRatio, width, height } = await validateHeaderAspectRatio(imageUrl);

        if (!isValid) {
            showToast(`Aspect ratio must be between 4:1 and 4.5:1. Current ratio: ${aspectRatio}:1 (${width}x${height}px).`, "error");
            setSavingSettings(false);
            return; // Prevent API submission
        }

        try {
            const payload = {
                store_id: modalSelectedStoreHeader,
                key: "invoice_header_url",
                value: invoiceHeaderData.attachmenturl,
                attachmentusage_id: "InvoiceHeader",
                metadata: {
                    filename: invoiceHeaderData.filename,
                    mimetype: invoiceHeaderData.mimetype,
                    filesize: invoiceHeaderData.filesize
                }
            };

            // console.log(payload)

            const response = await client.postData('/store/save-invoice-header', payload);
            showToast(response.msg || "Invoice header updated successfully!", "success");

            setIsHeaderModalOpen(false);
            setInvoiceHeaderData({ attachmenturl: '', filename: '', mimetype: '', mimetypeencoding: '', filesize: 0 });
            setModalSelectedStoreHeader('');
            loadGlobalSettings();
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to save invoice header.", "error");
        } finally {
            setSavingSettings(false);
        }
    }, [modalSelectedStoreHeader, invoiceHeaderData, showToast, loadGlobalSettings]);

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">You must be logged in to view user management.</p>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="mb-6 p-4 sm:p-6 shadow-lg rounded-lg">
                <div className="mb-6 border-b border-gray-200">
                    <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-4 overflow-x-auto no-scrollbar pb-1 sm:pb-0">
                        {/* Tab button for User Management */}
                        <button
                            onClick={() => setActiveTab('users')}
                            className={`py-2 px-3 sm:py-2 sm:px-4 text-sm sm:text-lg font-semibold flex items-center gap-2 whitespace-nowrap transition-colors duration-200
                ${activeTab === 'users' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                        >
                            <UsersIcon size={20} className="sm:w-[24px] sm:h-[24px]" /> User Management
                        </button>

                        {/* Tab button for Activity Logs */}
                        <button
                            onClick={() => setActiveTab('auditLogs')}
                            className={`py-2 px-3 sm:py-2 sm:px-4 text-sm sm:text-lg font-semibold flex items-center gap-2 whitespace-nowrap transition-colors duration-200
                ${activeTab === 'auditLogs' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                        >
                            <BarChart2 size={20} className="sm:w-[24px] sm:h-[24px]" /> Activity Logs
                        </button>

                        {/* Tab button for Roles */}
                        <button
                            onClick={() => setActiveTab('roles')}
                            className={`py-2 px-3 sm:py-2 sm:px-4 text-sm sm:text-lg font-semibold flex items-center gap-2 whitespace-nowrap transition-colors duration-200
                ${activeTab === 'roles' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                        >
                            <ShieldCheck size={20} className="sm:w-[24px] sm:h-[24px]" /> Roles
                        </button>

                        {/* Tab button for endpoint RBAC */}
                        <button
                            onClick={() => setActiveTab('endpointPermissions')}
                            className={`py-2 px-3 sm:py-2 sm:px-4 text-sm sm:text-lg font-semibold flex items-center gap-2 whitespace-nowrap transition-colors duration-200
                ${activeTab === 'endpointPermissions' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                        >
                            <UserLock size={20} className="sm:w-[24px] sm:h-[24px]" /> Permissions
                        </button>
                        <button
                            onClick={() => setActiveTab('settings')}
                            className={`py-2 px-3 sm:py-2 sm:px-4 text-sm sm:text-lg font-semibold flex items-center gap-2 whitespace-nowrap transition-colors duration-200
                                        ${activeTab === 'settings' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                        >
                            <Cog size={20} className="sm:w-[24px] sm:h-[24px]" />Settings
                        </button>
                    </div>
                </div>

                {/* Conditional rendering based on the active tab */}
                {activeTab === 'users' && (
                    <>
                        <p className="text-text-secondary mb-6">
                            View and manage all members (users) registered in the system, including their roles and types.
                        </p>

                        <div className="flex justify-between items-center mb-6">
                            <Dropdown
                                trigger={
                                    <Button variant="primary" className="flex items-center gap-2">
                                        <UserPlus size={20} /> Create New <ChevronDown size={20} />
                                    </Button>
                                }
                                align="right"
                            >
                                <div className="py-1">
                                    <button
                                        onClick={() => navigate('/settings/new-customer')}
                                        className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        <UserCheck size={16} /> Add New Customer
                                    </button>
                                    <button
                                        onClick={() => navigate('/settings/invite-staff')}
                                        className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        <MailPlus size={16} /> Invite New Staff
                                    </button>
                                    <button
                                        onClick={() => navigate('/branches/staff-to-branch')}
                                        className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        <GitBranch size={16} /> Add Staff to Branch
                                    </button>
                                </div>
                            </Dropdown>
                        </div>

                        {loadingMembers ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                                <p className="text-gray-600">Loading users...</p>
                            </div>
                        ) : errorMembers ? (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                                <p>{errorMembers}</p>
                                <Button onClick={loadMembers} variant="secondary" className="mt-2">Retry</Button>
                            </div>
                        ) : (
                            <DataTable2
                                columns={memberColumns}
                                data={members}
                                loading={loadingMembers}
                                error={errorMembers}
                                emptyMessage="No users found in the system."
                                enableSearch={true}
                                enablePagination={true}
                                rowsPerPage={10}
                                onEdit={handleEditUser}
                                onDelete={handleDeleteUser}
                                rowActions={userRowActions}
                                onAction={handleUserAction}
                            />
                        )}
                    </>
                )}

                {activeTab == "roles" && (
                    <>
                        <p className="text-text-secondary mb-6">
                            View and manage roles used system-wide.
                        </p>
                        <div className="flex justify-between items-center mb-6">
                            <Button variant="primary" className="flex items-center gap-2"
                                onClick={() => setIsCreateRoleModalOpen(true)}
                            >
                                <ShieldPlus size={20} /> Create New
                            </Button>
                        </div>
                        {loadingRoles ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                                <p className="text-gray-600">Loading roles...</p>
                            </div>
                        ) : errorRoles ? (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                                <p>{errorRoles}</p>
                                <Button onClick={loadRoles} variant="secondary" className="mt-2">Retry</Button>
                            </div>
                        ) : (
                            <>
                                <DataTable2
                                    columns={rolesColumns}
                                    data={roles}
                                    loading={loadingRoles}
                                    error={errorRoles}
                                    emptyMessage="No system roles available."
                                    enableSearch={true}
                                    enablePagination={true}
                                    rowActions={rolesRowActions}
                                    onAction={handleRoleAction}
                                />
                            </>
                        )}
                        <Modal
                            isOpen={isCreateRoleModalOpen}
                            onClose={() => {
                                setIsCreateRoleModalOpen(false);
                                setNewRoleName('');
                                setNewRoleDescription('');
                                setNewRoleEditable(true);
                            }}
                            title="Create New Role"
                            size="md"
                        >
                            <div className="space-y-6">
                                <Input
                                    label="Role Name"
                                    type="text"
                                    value={newRoleName}
                                    onChange={(e) => setNewRoleName(e.target.value)}
                                    placeholder="e.g., Manager, Accountant"
                                    required
                                />

                                <Input
                                    label="Brief Description"
                                    type="text"
                                    value={newRoleDescription}
                                    onChange={(e) => setNewRoleDescription(e.target.value)}
                                    placeholder="Short description of what this role can do"
                                />

                                <Checkbox
                                    label="Editable (can be modified later)"
                                    checked={newRoleEditable}
                                    onChange={(e) => setNewRoleEditable(e.target.checked)}
                                />

                                <div className="flex justify-end gap-3 pt-4">
                                    <Button
                                        variant="secondary"
                                        onClick={() => setIsCreateRoleModalOpen(false)}
                                        disabled={creatingRole}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleCreateRole}
                                        disabled={creatingRole}
                                    >
                                        {creatingRole ? 'Creating...' : 'Create Role'}
                                    </Button>
                                </div>
                            </div>
                        </Modal>
                    </>
                )}

                {activeTab === 'endpointPermissions' && (
                    <>
                        <p className="text-text-secondary mb-6">
                            View all roles based permissions for system resources.
                        </p>
                        {loadingEndpointPermissions ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                                <p className="text-gray-600">Loading endpoint permissions...</p>
                            </div>
                        ) : errorEndpointPermissions ? (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                                <p>{errorEndpointPermissions}</p>
                                <Button onClick={loadCurrentEndpointPermissions} variant="secondary" className="mt-2">Retry</Button>
                            </div>
                        ) : (
                            <>
                                <DataTable2
                                    columns={permissionsListColumns}
                                    data={endpointPermissions}
                                    loading={loadingEndpointPermissions}
                                    error={errorEndpointPermissions}
                                    emptyMessage="No endpoint permissions available."
                                    enableSearch={true}
                                    enablePagination={true}
                                    rowsPerPage={10}
                                    rowActions={permissionRowActions}
                                    onAction={handlePermissionAction}
                                />
                                <Modal
                                    isOpen={isModalOpen}
                                    onClose={() => setIsModalOpen(false)}
                                    title={`Modify Permissions for ${selectedEndpoint?.endpoint_path || ''}`}
                                    size="md">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Select Roles
                                            </label>
                                            <Select
                                                isMulti
                                                options={availableRoles}
                                                value={selectedRoles}
                                                onChange={handleRoleSelect}
                                                placeholder="Search and select roles..."
                                                className="basic-multi-select"
                                                classNamePrefix="select"
                                            />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                                                Cancel
                                            </Button>
                                            <Button variant="primary" onClick={handleSavePermissions}>
                                                Save
                                            </Button>
                                        </div>
                                    </div>
                                </Modal>
                            </>
                        )}
                    </>
                )}

                {activeTab === 'auditLogs' && (
                    <>
                        <p className="text-text-secondary mb-6">
                            View all user activities and events in the system.
                        </p>
                        {loadingAuditLogs ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                                <p className="text-gray-600">Loading activity logs...</p>
                            </div>
                        ) : errorAuditLogs ? (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                                <p>{errorAuditLogs}</p>
                                <Button onClick={loadAuditLogs} variant="secondary" className="mt-2">Retry</Button>
                            </div>
                        ) : (
                            <DataTable2
                                columns={auditLogColumns}
                                data={auditLogs}
                                loading={loadingAuditLogs}
                                error={errorAuditLogs}
                                emptyMessage="No activity logs found."
                                enableSearch={true}
                                enablePagination={true}
                                rowsPerPage={10}
                            />
                        )}
                    </>
                )}

                {activeTab === 'settings' && (
                    <>
                        {/* Section 1: Branch Portal Address */}
                        <div className="p-6 bg-white">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="text-lg font-semibold text-text-primary">Branch Portal Address (URL)</h4>
                                    <p className="text-text-secondary text-sm">
                                        Manage URL routing attributes based on stores you are explicitly assigned to.
                                    </p>
                                    <span className="inline-block mt-2 text-xs font-mono text-text-secondary bg-gray-50 px-2 py-1 rounded border border-gray-100">
                                        Key parameter: branch_portal_address
                                    </span>
                                </div>
                                <Button
                                    variant="primary"
                                    className="flex items-center gap-2"
                                    onClick={() => {
                                        // if (stores.length > 0) setModalSelectedStore(stores[0].value);
                                        // setIsSettingsModalOpen(true);
                                        const initialStore = stores[0]?.value || '';
                                        setModalSelectedStore(initialStore);
                                        setPortalAddress(getSettingValue(initialStore, "branch_portal_address"));
                                        setIsSettingsModalOpen(true);
                                    }}
                                >
                                    <span>+ Set Portal Address</span>
                                </Button>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                                <span className="text-sm text-text-secondary font-medium">Current Values by Store:</span>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm divide-y divide-gray-200">
                                        <thead>
                                            <tr className="text-left text-gray-500">
                                                <th className="pb-2 font-medium">Store / Branch</th>
                                                <th className="pb-2 font-medium">Value</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {stores.map(store => (
                                                <tr key={store.value}>
                                                    <td className="py-2 text-gray-700 font-medium">{store.label}</td>
                                                    <td className="py-2 font-mono text-gray-600">{getSettingValue(store.value, "branch_portal_address") || <span className="text-gray-300 italic">Not set</span>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Separator Line */}
                        <hr className="border-gray-200" />

                        {/* Section 2: Branch PSKey */}
                        <div className="p-6 bg-white">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="text-lg font-semibold text-text-primary">Payment Portal Private Keys (Secret)</h4>
                                    <p className="text-text-secondary text-sm">
                                        Manage secure private encryption and payload keys for remote branches.
                                    </p>
                                    {/* <span className="inline-block mt-2 text-xs font-mono text-text-secondary bg-gray-50 px-2 py-1 rounded border border-gray-100">
                                        Key parameter: PSKey
                                    </span> */}
                                </div>
                                <Button
                                    variant="primary"
                                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 border-none"
                                    onClick={() => {
                                        const initialStore = stores[0]?.value || '';
                                        setModalSelectedStorePs(initialStore);
                                        setPsKeyName('PSKey'); // Default fallback setting
                                        // setPsKey(getSettingValue(initialStore, "PSKey"));
                                        setIsPsModalOpen(true);
                                        // if (stores.length > 0) setModalSelectedStorePs(stores[0].value);
                                        // setIsPsModalOpen(true);
                                    }}
                                >
                                    <span>Configure PSKey</span>
                                </Button>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                                <span className="text-sm text-text-secondary font-medium">Current Values by Store:</span>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm divide-y divide-gray-200">
                                        <thead>
                                            <tr className="text-left text-gray-500">
                                                <th className="pb-2 font-medium">Store / Branch</th>
                                                <th className="pb-2 font-medium">Value Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {stores.map(store => {
                                                const hasVal = !!getSettingValue(store.value, "PSKey");
                                                return (
                                                    <tr key={store.value}>
                                                        <td className="py-2 text-gray-700 font-medium">{store.label}</td>
                                                        <td className="py-2">
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${hasVal ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-gray-100 text-gray-400'}`}>
                                                                {hasVal ? '● Configured (Hidden)' : 'Not Set'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        <div className="p-6 bg-white">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="text-lg font-semibold text-text-primary">Payment Portal Public Keys (Processing)</h4>
                                    <p className="text-text-secondary text-sm">
                                        Manage secure payload validation and provider processing keys for remote branches.
                                    </p>
                                    {/* <span className="inline-block mt-2 text-xs font-mono text-text-secondary bg-gray-50 px-2 py-1 rounded border border-gray-100">
                                        Key parameter: PPKey
                                    </span> */}
                                </div>
                                <Button
                                    variant="primary"
                                    className="flex items-center gap-2 bg-indigo-800 hover:bg-indigo-900 border-none"
                                    onClick={() => {
                                        const initialStore = stores[0]?.value || '';
                                        setModalSelectedStorePs(initialStore);
                                        // setPsKey(getSettingValue(initialStore, "PPKey"));
                                        setPpKeyName('PPKey'); // Default fallback setting
                                        setIsPpModalOpen(true);
                                        // if (stores.length > 0) setModalSelectedStorePp(stores[0].value);
                                        // setIsPpModalOpen(true);
                                    }}
                                >
                                    <span>Conigure PPKey</span>
                                </Button>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                                <span className="text-sm text-text-secondary font-medium">Current Values by Store:</span>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm divide-y divide-gray-200">
                                        <thead>
                                            <tr className="text-left text-gray-500">
                                                <th className="pb-2 font-medium">Store / Branch</th>
                                                <th className="pb-2 font-medium">Value Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {stores.map(store => {
                                                const hasVal = !!getSettingValue(store.value, "PPKey");
                                                return (
                                                    <tr key={store.value}>
                                                        <td className="py-2 text-gray-700 font-medium">{store.label}</td>
                                                        <td className="py-2">
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${hasVal ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-gray-100 text-gray-400'}`}>
                                                                {hasVal ? '● Configured (Hidden)' : 'Not Set'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        <div className="p-6 bg-white">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="text-lg font-semibold text-text-primary">Settlement Gateway Account</h4>
                                    <p className="text-text-secondary text-sm">
                                        Configure the destination bank account where funds aggregated via web payment will be routed.
                                    </p>
                                </div>
                                <Button
                                    variant="primary"
                                    className="flex items-center gap-2 bg-teal-700 hover:bg-teal-800 border-none"
                                    onClick={() => {
                                        const initialStore = stores[0]?.value || '';
                                        setModalSelectedStorePsGateway(initialStore);
                                        // Pre-fill the bank account if it has already been configured for this store
                                        setSelectedBankAccount(getPaystackBankAccount(initialStore));
                                        setIsPaystackModalOpen(true);
                                        // if (stores.length > 0) setModalSelectedStorePsGateway(stores[0].value);
                                        // setIsPaystackModalOpen(true);
                                    }}
                                >
                                    <span className='flex items-center justify-between gap-2'>
                                        <Pencil size={18} />
                                        Set Settlement Account
                                    </span>
                                </Button>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                                <span className="text-sm text-text-secondary font-medium">Current Settlement Accounts by Store:</span>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm divide-y divide-gray-200">
                                        <thead>
                                            <tr className="text-left text-gray-500">
                                                <th className="pb-2 font-medium">Store / Branch</th>
                                                <th className="pb-2 font-medium">Gateway Name</th>
                                                <th className="pb-2 font-medium">Settlement Bank Account</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {stores.map(store => {
                                                const processors = getPaymentProcessorsForStore(store.value);

                                                if (processors.length === 0) {
                                                    return (
                                                        <tr key={store.value}>
                                                            <td className="py-2 text-gray-700 font-medium">{store.label}</td>
                                                            <td className="py-2 text-gray-400 italic" colSpan={2}>
                                                                No payment processors configured.
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                return processors.map((proc, idx) => {
                                                    const accountOption = bankAccountOptions.find(
                                                        opt => Number(opt.value) === Number(proc.bankaccount_id)
                                                    );

                                                    return (
                                                        <tr key={`${store.value}-${proc.paymentprocessor_id || proc.name}-${idx}`}>
                                                            <td className="py-2 text-gray-700 font-medium">
                                                                {idx === 0 ? store.label : ''}
                                                            </td>
                                                            <td className="py-2">
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                                                                    {proc.name}
                                                                </span>
                                                            </td>
                                                            <td className="py-2 font-mono text-gray-600">
                                                                {accountOption ? (
                                                                    accountOption.label
                                                                ) : proc.bankaccount_id ? (
                                                                    `Account ID: ${proc.bankaccount_id}`
                                                                ) : (
                                                                    <span className="text-gray-300 italic">Not configured</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        <div className="p-6 bg-white">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="text-lg font-semibold text-text-primary">Invoice & Letterhead Header (4:1 Aspect Ratio)</h4>
                                    <p className="text-text-secondary text-sm">
                                        Upload official brand banners (attachmentusage_id: "InvoiceHeader") for PDF export headers.
                                    </p>
                                </div>
                                <Button
                                    variant="primary"
                                    className="flex items-center gap-2 bg-purple-700 hover:bg-purple-800 border-none"
                                    onClick={() => {
                                        const initialStore = stores[0]?.value || '';
                                        setModalSelectedStoreHeader(initialStore);
                                        setIsHeaderModalOpen(true);
                                    }}
                                >
                                    <span>Configure Invoice Header</span>
                                </Button>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                                <span className="text-sm text-text-secondary font-medium">Configured Banners by Store:</span>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm divide-y divide-gray-200">
                                        <thead>
                                            <tr className="text-left text-gray-500">
                                                <th className="pb-2 font-medium">Store / Branch</th>
                                                <th className="pb-2 font-medium">Header Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {stores.map(store => {
                                                const url = getSettingValue(store.value, "invoice_header_url");
                                                return (
                                                    <tr key={store.value}>
                                                        <td className="py-2 text-gray-700 font-medium">{store.label}</td>
                                                        <td className="py-2 font-mono text-gray-600">
                                                            {url ? (
                                                                <span className="text-emerald-600 font-semibold">● Configured</span>
                                                            ) : (
                                                                <span className="text-gray-300 italic">Not set</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        <div className="p-6 bg-white">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="text-lg font-semibold text-text-primary">Revenue Recognition Schedule (ASC 606)</h4>
                                    <p className="text-text-secondary text-sm">
                                        Configure accounting recognition and period types matching your compliance strategy.
                                    </p>
                                </div>
                                <Button
                                    variant="primary"
                                    className="flex items-center gap-2 bg-emerald-800 hover:bg-emerald-900 border-none"
                                    onClick={() => {
                                        const initialStore = stores[0]?.value || '';
                                        setModalSelectedStoreRev(initialStore);
                                        setRevKeyName('RevenueRecognitionType');
                                        setIsRevModalOpen(true);
                                    }}
                                >
                                    <span>Configure Revenue Schedule</span>
                                </Button>
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                                <span className="text-sm text-text-secondary font-medium">Current Configurations by Store:</span>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm divide-y divide-gray-200">
                                        <thead>
                                            <tr className="text-left text-gray-500">
                                                <th className="pb-2 font-medium">Store / Branch</th>
                                                <th className="pb-2 font-medium">Recognition Scheme</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {stores.map(store => {
                                                const val = getSettingValue(store.value, "RevenueRecognitionType");
                                                return (
                                                    <tr key={store.value}>
                                                        <td className="py-2 text-gray-700 font-medium">{store.label}</td>
                                                        <td className="py-2 font-mono text-gray-600">
                                                            {val ? val : <span className="text-gray-300 italic">Not configured</span>}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        <div className="p-6 bg-white">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="text-lg font-semibold text-text-primary">POS Terminal Management</h4>
                                    <p className="text-text-secondary text-sm">
                                        Create unique POS terminal records and assign staff members to registered terminals.
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="secondary"
                                        className="flex items-center gap-2"
                                        onClick={() => {
                                            const initialStore = stores[0]?.value || '';
                                            setCreateTerminalStoreId(initialStore);
                                            setNewTerminalId('');
                                            setNewTerminalSn('');
                                            setIsCreateTerminalModalOpen(true);
                                        }}
                                    >
                                        <span>+ Create POS Terminal</span>
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="flex items-center gap-2 bg-slate-700 hover:bg-slate-800 border-none"
                                        onClick={() => {
                                            const initialStore = stores[0]?.value || '';
                                            setModalSelectedStoreTerminal(initialStore);
                                            setSelectedEmployeeId('');
                                            setSelectedPosterminalId('');
                                            setIsTerminalModalOpen(true);
                                        }}
                                    >
                                        <span>Assign POS Terminal</span>
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Section: POS Terminal Settlement Mapping */}
                        <hr className="border-gray-200" />
                        <div className="p-6 bg-white">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="text-lg font-semibold text-text-primary">POS Terminal Bank Settlement Mapping</h4>
                                    <p className="text-text-secondary text-sm">
                                        Map specific POS terminals to designated bank accounts for fund settlement.
                                    </p>
                                </div>
                                <Button
                                    variant="primary"
                                    className="flex items-center gap-2 bg-amber-700 hover:bg-amber-800 border-none"
                                    onClick={() => {
                                        const initialStore = stores[0]?.value || '';
                                        setModalSelectedStorePosProc(initialStore);
                                        setSelectedMappingTerminalId('');
                                        setSelectedMappingBankAccount('');
                                        if (initialStore) {
                                            loadStoreTerminals(initialStore);
                                        }
                                        setIsPosProcessorModalOpen(true);
                                    }}
                                >
                                    <span className="flex items-center justify-between gap-2">
                                        <Pencil size={18} />
                                        Map POS Terminal to Bank
                                    </span>
                                </Button>
                            </div>
                        </div>


                        {/* Modal: POS Terminal Settlement Mapping */}
                        <Modal
                            isOpen={isPosProcessorModalOpen}
                            onClose={() => {
                                setIsPosProcessorModalOpen(false);
                                setModalSelectedStorePosProc('');
                                setSelectedMappingTerminalId('');
                                setSelectedMappingBankAccount('');
                            }}
                            title="Map POS Terminal to Settlement Bank Account"
                            size="md"
                        >
                            <div className="space-y-4">
                                <SearchableSelectBox
                                    name="modalStoreSelectPosProc"
                                    label="Select Store / Branch"
                                    options={stores}
                                    value={modalSelectedStorePosProc}
                                    onChange={(e) => {
                                        const storeId = e.target.value;
                                        setModalSelectedStorePosProc(storeId);
                                        setSelectedMappingTerminalId('');
                                        loadStoreTerminals(storeId);
                                    }}
                                    required
                                    loading={loadingStores}
                                    placeholder="Choose a store..."
                                />

                                <SearchableSelectBox
                                    name="modalTerminalSelectMapping"
                                    label="Select POS Terminal"
                                    options={storeTerminalOptions}
                                    value={selectedMappingTerminalId}
                                    onChange={(e) => {
                                        const val = e?.target ? e.target.value : e?.value || e;
                                        setSelectedMappingTerminalId(val);
                                    }}
                                    required
                                    loading={loadingStoreTerminals}
                                    placeholder={modalSelectedStorePosProc ? "Choose a terminal..." : "Select a store first..."}
                                    disabled={!modalSelectedStorePosProc}
                                />

                                <SearchableSelectBox
                                    name="modalBankAccountSelectMapping"
                                    label="Select Bank Account"
                                    options={bankAccountOptions}
                                    value={selectedMappingBankAccount}
                                    onChange={(e) => setSelectedMappingBankAccount(e.target.value)}
                                    required
                                    loading={loadingBankAccounts}
                                    placeholder="Choose a bank account..."
                                />

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                    <Button variant="secondary" onClick={() => setIsPosProcessorModalOpen(false)} disabled={savingSettings}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" onClick={handleSavePosTerminalProcessor} disabled={savingSettings}>
                                        {savingSettings ? 'Saving...' : 'Save Mapping'}
                                    </Button>
                                </div>
                            </div>
                        </Modal>


                        {/* Modal 1: Create POS Terminal Record */}
                        <Modal
                            isOpen={isCreateTerminalModalOpen}
                            onClose={() => {
                                setIsCreateTerminalModalOpen(false);
                                setCreateTerminalStoreId('');
                                setNewTerminalId('');
                                setNewTerminalSn('');
                            }}
                            title="Create POS Terminal Record"
                            size="md"
                        >
                            <div className="space-y-4">
                                <SearchableSelectBox
                                    name="createTerminalStoreSelect"
                                    label="Select Store"
                                    options={stores}
                                    value={createTerminalStoreId}
                                    onChange={(e) => setCreateTerminalStoreId(e.target.value)}
                                    required
                                    loading={loadingStores}
                                    placeholder="Choose a store..."
                                />

                                <Input
                                    label="Terminal ID"
                                    type="text"
                                    value={newTerminalId}
                                    onChange={(e) => setNewTerminalId(e.target.value)}
                                    placeholder="e.g., POS-001, CASHIER-MAIN"
                                    required
                                />

                                <Input
                                    label="Terminal Serial Number (terminal_sn)"
                                    type="text"
                                    value={newTerminalSn}
                                    onChange={(e) => setNewTerminalSn(e.target.value)}
                                    placeholder="e.g., SN-987654321"
                                    required
                                />

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setIsCreateTerminalModalOpen(false);
                                            setCreateTerminalStoreId('');
                                            setNewTerminalId('');
                                            setNewTerminalSn('');
                                        }}
                                        disabled={creatingTerminal}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleCreatePosTerminal}
                                        disabled={creatingTerminal}
                                    >
                                        {creatingTerminal ? 'Creating...' : 'Create Terminal'}
                                    </Button>
                                </div>
                            </div>
                        </Modal>

                        {/* Modal 2: Assign POS Terminal to Staff */}
                        <Modal
                            isOpen={isTerminalModalOpen}
                            onClose={() => {
                                setIsTerminalModalOpen(false);
                                setSelectedPosterminalId('');
                                setSelectedEmployeeId('');
                                setModalSelectedStoreTerminal('');
                            }}
                            title="Assign POS Terminal to Staff"
                            size="md"
                        >
                            <div className="space-y-4">
                                <SearchableSelectBox
                                    name="modalStoreSelectTerminal"
                                    label="Select Store"
                                    options={stores}
                                    value={modalSelectedStoreTerminal}
                                    onChange={(e) => setModalSelectedStoreTerminal(e.target.value)}
                                    required
                                    loading={loadingStores}
                                    placeholder="Choose a store..."
                                />

                                <SearchableSelectBox
                                    name="modalEmployeeSelectTerminal"
                                    label="Select Employee"
                                    options={employeeOptions}
                                    value={selectedEmployeeId}
                                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                                    required
                                    loading={loadingEmployees}
                                    placeholder={modalSelectedStoreTerminal ? "Choose an employee..." : "Select a store first..."}
                                    disabled={!modalSelectedStoreTerminal}
                                />

                                <SearchableSelectBox
                                    name="modalTerminalSelect"
                                    label="Select POS Terminal"
                                    options={storeTerminalOptions}
                                    value={selectedPosterminalId}
                                    onChange={(e) => {
                                        const val = e?.target ? e.target.value : e?.value || e;
                                        setSelectedPosterminalId(val);
                                    }}
                                    required
                                    loading={loadingStoreTerminals}
                                    placeholder={modalSelectedStoreTerminal ? "Choose a terminal..." : "Select a store first..."}
                                    disabled={!modalSelectedStoreTerminal}
                                />

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setIsTerminalModalOpen(false);
                                            setSelectedPosterminalId('');
                                            setSelectedEmployeeId('');
                                            setModalSelectedStoreTerminal('');
                                        }}
                                        disabled={savingSettings}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleSaveTerminalAssignment}
                                        disabled={savingSettings}
                                    >
                                        {savingSettings ? 'Saving...' : 'Assign Terminal'}
                                    </Button>
                                </div>
                            </div>
                        </Modal>

                        <Modal
                            isOpen={isSettingsModalOpen}
                            onClose={() => {
                                setIsSettingsModalOpen(false);
                                setPortalAddress('');
                            }}
                            title="Configure Branch Portal Address"
                            size="md"
                        >
                            <div className="space-y-4">
                                <SearchableSelectBox
                                    name="modalStoreSelect"
                                    label="Assign Target Store / Branch"
                                    options={stores}
                                    value={modalSelectedStore}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setModalSelectedStore(val);
                                        setPortalAddress(getSettingValue(val, "branch_portal_address"));
                                        // setModalSelectedStore(e.target.value)
                                    }}
                                    required
                                    loading={loadingStores}
                                    placeholder="Choose a store..."
                                />

                                <Input
                                    label="Portal Address URL"
                                    type="text"
                                    value={portalAddress}
                                    onChange={(e) => setPortalAddress(e.target.value)}
                                    placeholder="https://branch-portal.example.com"
                                    required
                                />

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                    <Button
                                        variant="secondary"
                                        onClick={() => setIsSettingsModalOpen(false)}
                                        disabled={savingSettings}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleSavePortalAddress}
                                        disabled={savingSettings}
                                    >
                                        {savingSettings ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                </div>
                            </div>
                        </Modal>
                        {/* Separate Modal implementation for PSKey configuration rendering */}
                        <Modal
                            isOpen={isPsModalOpen}
                            onClose={() => {
                                setIsPsModalOpen(false);
                                setPsKey('');
                            }}
                            title="Configure Payment Portal's Private/Secret Key"
                            size="md"
                        >
                            <div className="space-y-4">
                                <SearchableSelectBox
                                    name="modalStoreSelectPs"
                                    label="Assign Target Store / Branch"
                                    options={stores}
                                    value={modalSelectedStorePs}
                                    onChange={(e) => {
                                        // const val = e.target.value;
                                        // setModalSelectedStorePs(val);
                                        // setPsKey(getSettingValue(val, "PSKey"));
                                        setModalSelectedStorePs(e.target.value)
                                    }}
                                    required
                                    loading={loadingStores}
                                    placeholder="Choose a store..."
                                />

                                <Input
                                    label="Key Parameter Attribute Name"
                                    type="text"
                                    value={psKeyName}
                                    onChange={(e) => setPsKeyName(e.target.value)}
                                    placeholder="e.g., PSKey, SSKey"
                                    required
                                />

                                <Input
                                    label="Private Key Value"
                                    type={showPassword ? "text" : "password"}
                                    value={psKey}
                                    onChange={(e) => setPsKey(e.target.value)}
                                    placeholder="Enter your private encryption secret key value"
                                    required
                                    trailingIcon={
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="text-gray-500 hover:text-gray-700 focus:outline-none pointer-events-auto"
                                            aria-label={showPassword ? "Hide Password" : "Show Password"}
                                        >
                                            {showPassword ? (
                                                // Eye slashed (hidden)
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                    <path d="M1 1l22 22" />
                                                </svg>
                                            ) : (
                                                // Eye open (visible)
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                            )}
                                        </button>
                                    }
                                />

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                    <Button variant="secondary" onClick={() => setIsPsModalOpen(false)} disabled={savingSettings}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" onClick={handleSavePsKey} disabled={savingSettings}>
                                        {savingSettings ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                </div>
                            </div>
                        </Modal>
                        <Modal
                            isOpen={isPpModalOpen}
                            onClose={() => {
                                setIsPpModalOpen(false);
                                setPpKey('');
                            }}
                            title="Configure Payment Portal's Public/Processing Key"
                            size="md"
                        >
                            <div className="space-y-4">
                                <SearchableSelectBox
                                    name="modalStoreSelectPp"
                                    label="Assign Target Store / Branch"
                                    options={stores}
                                    value={modalSelectedStorePp}
                                    onChange={(e) => setModalSelectedStorePp(e.target.value)}
                                    required
                                    loading={loadingStores}
                                    placeholder="Choose a store..."
                                />

                                <Input
                                    label="Key Parameter Attribute Name"
                                    type="text"
                                    value={ppKeyName}
                                    onChange={(e) => setPpKeyName(e.target.value)}
                                    placeholder="e.g., PPKey, SPKey"
                                    required
                                />

                                <Input
                                    label="Public Key Value"
                                    type={showPpPassword ? "text" : "password"}
                                    value={ppKey}
                                    onChange={(e) => setPpKey(e.target.value)}
                                    placeholder="Enter your processing public key value"
                                    required
                                    trailingIcon={
                                        <button
                                            type="button"
                                            onClick={() => setShowPpPassword(!showPpPassword)}
                                            className="text-gray-500 hover:text-gray-700 focus:outline-none pointer-events-auto"
                                            aria-label={showPpPassword ? "Hide Password" : "Show Password"}
                                        >
                                            {showPpPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                    <path d="M1 1l22 22" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                            )}
                                        </button>
                                    }
                                />

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                    <Button variant="secondary" onClick={() => setIsPpModalOpen(false)} disabled={savingSettings}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" onClick={handleSavePpKey} disabled={savingSettings}>
                                        {savingSettings ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                </div>
                            </div>
                        </Modal>
                        <Modal
                            isOpen={isPaystackModalOpen}
                            onClose={() => {
                                setIsPaystackModalOpen(false);
                                setSelectedBankAccount('');
                                setGatewayName('Paystack');
                            }}
                            title="Configure Payment Settlement Gateway"
                            size="md"
                        >
                            <div className="space-y-4">
                                <SearchableSelectBox
                                    name="modalStoreSelectPsGateway"
                                    label="Assign Target Store / Branch"
                                    options={stores}
                                    value={modalSelectedStorePsGateway}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setModalSelectedStorePsGateway(val);
                                        // Sync bank account based on store AND active gatewayName
                                        setSelectedBankAccount(getBankAccountForGateway(val, gatewayName));
                                    }}
                                    required
                                    loading={loadingStores}
                                    placeholder="Choose a store..."
                                />

                                <Input
                                    label="Payment Gateway Name"
                                    type="text"
                                    value={gatewayName}
                                    onChange={(e) => {
                                        const nameVal = e.target.value;
                                        setGatewayName(nameVal);
                                        setSelectedBankAccount(getBankAccountForGateway(modalSelectedStorePsGateway, nameVal));
                                    }}
                                    placeholder="e.g., Paystack, Seerbit"
                                    required
                                />

                                <SearchableSelectBox
                                    name="modalBankAccountSelect"
                                    label="Select Settlement Bank Account"
                                    options={bankAccountOptions}
                                    value={selectedBankAccount}
                                    onChange={(e) => setSelectedBankAccount(e.target.value)}
                                    required
                                    loading={loadingBankAccounts}
                                    placeholder="Choose a bank account..."
                                />

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                    <Button variant="secondary" onClick={() => setIsPaystackModalOpen(false)} disabled={savingSettings}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" onClick={handleSavePaystackGateway} disabled={savingSettings}>
                                        {savingSettings ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                </div>
                            </div>
                        </Modal>
                        <Modal
                            isOpen={isRevModalOpen}
                            onClose={() => {
                                setIsRevModalOpen(false);
                                setRevenueRecognition('');
                            }}
                            title="Configure Revenue Recognition Schedule"
                            size="md"
                        >
                            <div className="space-y-4">
                                <SearchableSelectBox
                                    name="modalStoreSelectRev"
                                    label="Assign Target Store / Branch"
                                    options={stores}
                                    value={modalSelectedStoreRev}
                                    onChange={(e) => setModalSelectedStoreRev(e.target.value)}
                                    required
                                    loading={loadingStores}
                                    placeholder="Choose a store..."
                                />

                                <Input
                                    label="Key Parameter Attribute Name"
                                    type="text"
                                    value={revKeyName}
                                    onChange={(e) => setRevKeyName(e.target.value)}
                                    placeholder="e.g., RevenueRecognitionType"
                                    required
                                />

                                <div className="flex flex-col">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        ASC 606 Period Recognition Type
                                    </label>
                                    <select
                                        value={revenueRecognition}
                                        onChange={(e) => setRevenueRecognition(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="" disabled>Select period scheme...</option>
                                        <option value="Daily">Daily Recognition</option>
                                        <option value="Monthly">Monthly (Straight Line)</option>
                                        <option value="Quarterly">Quarterly</option>
                                        <option value="Annually">Annually</option>
                                        <option value="Point-In-Time">Point of Sale (Immediate)</option>
                                    </select>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                    <Button variant="secondary" onClick={() => setIsRevModalOpen(false)} disabled={savingSettings}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" onClick={handleSaveRevSetting} disabled={savingSettings}>
                                        {savingSettings ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                </div>
                            </div>
                        </Modal>
                        <Modal
                            isOpen={isHeaderModalOpen}
                            onClose={() => {
                                setIsHeaderModalOpen(false);
                                setInvoiceHeaderData({ attachmenturl: '', filename: '', mimetype: '', mimetypeencoding: '', filesize: 0 });
                            }}
                            title="Configure Invoice & Letterhead Banner"
                            size="md"
                        >
                            <div className="space-y-4">
                                <SearchableSelectBox
                                    name="modalStoreSelectHeader"
                                    label="Assign Target Store / Branch"
                                    options={stores}
                                    value={modalSelectedStoreHeader}
                                    onChange={(e) => setModalSelectedStoreHeader(e.target.value)}
                                    required
                                    loading={loadingStores}
                                    placeholder="Choose a store..."
                                />

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Upload Banner (Aspect Ratio ~4:1, PNG or JPG)
                                    </label>
                                    <DropZone
                                        label="Drag & drop invoice header banner (4:1 to 4.5:1 aspect ratio)"
                                        accept=".jpg,.png,.jpeg"
                                        uploadUrl={`${API_BASE_PATH}/uploads/upload`}
                                        onBeforeUpload={async (file) => {
                                            const { isValid, aspectRatio, width, height } = await validateHeaderAspectRatio(file);
                                            if (!isValid) {
                                                showToast(`Upload blocked: Aspect ratio (${aspectRatio}:1) is outside the allowed range of 4.0 to 4.5 (${width}x${height}px).`, "error");
                                                return false; // Blocks upload to server
                                            }
                                            return true;
                                        }}
                                        onUploadComplete={handleInvoiceHeaderUploadComplete}
                                    />
                                </div>

                                {invoiceHeaderData.attachmenturl && (
                                    <div className="p-2 border border-gray-200 rounded-md bg-gray-50">
                                        <p className="text-xs text-gray-500 mb-1">Uploaded Preview:</p>
                                        <img
                                            // src={invoiceHeaderData.attachmenturl}
                                            src={`${API_ROOT_PATH}${invoiceHeaderData.attachmenturl}`}
                                            alt="Invoice Header Preview"
                                            className="w-full h-auto max-h-24 object-contain rounded border border-gray-200"
                                        />
                                    </div>
                                )}

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                    <Button variant="secondary" onClick={() => setIsHeaderModalOpen(false)} disabled={savingSettings}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" onClick={handleSaveInvoiceHeader} disabled={savingSettings}>
                                        {savingSettings ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                </div>
                            </div>
                        </Modal>
                    </>
                )}
            </Card>
        </div>
    );
};

export default Users;
